// src/admin/pages/supplier/SuppliersManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Pagination,
  Backdrop,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import { Supplier } from '../purchase-orders/PurchaseOrder';
import SupplierDrawer from './SupplierDialog';
import SupplierViewDrawer from './SupplierViewDialog';
import {
  useSuppliersPaginated,
  useSupplierStats,
  useSupplierMutations,
} from '../../../hooks/useSuppliers';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';

const SUPPLIERS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading suppliers...
        </Typography>
      </Box>
    </Box>
  );
};

const SuppliersManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  // ── UI state ──────────────────────────────────────────────────────────────
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // ── Delete confirmation dialog state ──────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Full-screen delete overlay (mirrors EnhancedProductDrawer pattern) ────
  const [deletingOverlay, setDeletingOverlay] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { hasPermission, isSuperAdmin } = useAuth();

const canView   = isSuperAdmin || hasPermission('supplier.view');
const canCreate = isSuperAdmin || hasPermission('supplier.create');
const canEdit   = isSuperAdmin || hasPermission('supplier.edit');
const canDelete = isSuperAdmin || hasPermission('supplier.delete');

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: suppliersData,
    isLoading: suppliersLoading,
    isFetching: suppliersIsFetching,
    isError: suppliersIsError,
    error: suppliersErrorObj,
  } = useSuppliersPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    pageSize: SUPPLIERS_PER_PAGE,
  });

  const { data: statsData } = useSupplierStats();
  const { remove } = useSupplierMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const suppliers = suppliersData?.suppliers ?? [];
  const totalSuppliers = suppliersData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalSuppliers / SUPPLIERS_PER_PAGE);
  const totalCount = statsData?.totalSuppliers ?? 0;
  const activeCount = statsData?.activeSuppliers ?? 0;
  const inactiveCount = statsData?.inactiveSuppliers ?? 0;
  const loading = suppliersLoading || suppliersIsFetching;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteSupplier = (supplier: Supplier) => {
    setDeleteTarget(supplier);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      // Close the confirm dialog immediately, then show the full-screen overlay
      // while the async work completes — mirrors EnhancedProductDrawer pattern.
      setDeleteTarget(null);
      setDeletingOverlay(true);

      await remove.mutateAsync(deleteTarget.id!);

      resetToFirstPage();
      showToast('Supplier deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete supplier: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
      setDeletingOverlay(false);
    }
  };

  if (!canView) {
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Diversity3Icon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
          <Typography variant="h5" gutterBottom>Access Denied</Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to view suppliers. Please contact your administrator.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── View / Edit ───────────────────────────────────────────────────────────
  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setViewDrawerOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    if (!canEdit) return;
    setSelectedSupplier(supplier);
    setEditDrawerOpen(true);
  };

  // ── Called by SupplierDrawer after successful create/edit ─────────────────
  const handleDrawerSuccess = () => {
    resetToFirstPage();
  };

  // ── Refresh ───────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    resetToFirstPage();
    showToast('Suppliers refreshed', 'info');
  };

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && suppliers.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading suppliers...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Diversity3Icon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Suppliers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your supplier information
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
  <Button
    variant="contained"
    startIcon={<AddIcon />}
    onClick={() => setCreateDrawerOpen(true)}
    sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
  >
    Add Supplier
  </Button>
)}
        </Stack>

        {suppliersIsError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {(suppliersErrorObj as Error)?.message || 'Failed to fetch suppliers'}
          </Alert>
        )}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Total Suppliers</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {totalCount}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{activeCount} Active</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>{inactiveCount} Inactive</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Active</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {activeCount}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalCount > 0 ? `${((activeCount / totalCount) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Inactive</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {inactiveCount}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalCount > 0 ? `${((inactiveCount / totalCount) * 100).toFixed(1)}% suspended` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                  {hasActiveFilters ? 'Matching Results' : 'This Page'}
                </Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {suppliers.length}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {hasActiveFilters
                      ? `of ${totalSuppliers} total`
                      : `page ${currentPage} of ${Math.max(1, totalPages)}`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search and Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search suppliers... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="inactive">Inactive Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${suppliers.length} suppliers matching your filters`
                        : `Showing ${suppliers.length} suppliers on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {suppliers.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Suppliers Match Your Filters' : 'No Suppliers Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No suppliers available yet. Add your first supplier to get started.'}
                </Typography>
                {hasActiveFilters ? (
  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
    Clear Filters
  </Button>
) : canCreate && (
  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDrawerOpen(true)}>
    Add Supplier
  </Button>
)}
              </CardContent>
            </Card>
          </Box>
        )}

        {suppliers.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 600, md: 800 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Contact Person</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Phone</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Payment Terms</TableCell>
                    <TableCell>Status</TableCell>
                    {(canView || canEdit || canDelete) && (
  <TableCell align="right">Actions</TableCell>
)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suppliers.map((supplier) => (
                    <TableRow
                      key={supplier.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                            <BusinessIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                              {supplier.name}
                            </Typography>
                            {supplier.taxId && (
                              <Typography variant="caption" color="text.secondary">
                                Tax ID: {supplier.taxId}
                              </Typography>
                            )}
                            {supplier.contactPerson && (
                              <Typography variant="caption" color="text.secondary"
                                sx={{ display: { xs: 'block', sm: 'none' } }}>
                                {supplier.contactPerson}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary"
                              sx={{ display: { xs: 'block', md: 'none' } }}>
                              {supplier.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {supplier.contactPerson ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">{supplier.contactPerson}</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Not specified</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{supplier.email}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{supplier.phone}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {supplier.paymentTerms ? (
                          <Chip label={supplier.paymentTerms} size="small" variant="outlined"
                            sx={getSoftChipSx('info', isDark)} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">Not set</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={supplier.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(supplier.isActive ? 'success' : 'default', isDark)}
                        />
                      </TableCell>

                      {(canView || canEdit || canDelete) && (
  <TableCell align="right">
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
      {canView && (
        <Tooltip title="View Details">
          <IconButton onClick={() => handleViewSupplier(supplier)} size="small" color="primary">
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip title="Edit">
          <IconButton onClick={() => handleEditSupplier(supplier)} size="small" color="warning">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title="Delete">
          <IconButton
            onClick={() => handleDeleteSupplier(supplier)}
            disabled={deleteLoading === supplier.id}
            size="small" color="error"
          >
            {deleteLoading === supplier.id
              ? <CircularProgress size={16} />
              : <DeleteIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </TableCell>
)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalSuppliers === 0 ? 0 : (currentPage - 1) * SUPPLIERS_PER_PAGE + 1}–
                    {Math.min(currentPage * SUPPLIERS_PER_PAGE, totalSuppliers)} of {totalSuppliers} suppliers
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Create Supplier Drawer ── */}
      <SupplierDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        onSuccess={handleDrawerSuccess}
        onShowToast={showToast}
      />

      {/* ── Edit / View Supplier Drawers (only when a supplier is selected) ── */}
      {selectedSupplier && (
        <>
          <SupplierDrawer
            open={editDrawerOpen}
            onClose={() => { setEditDrawerOpen(false); setSelectedSupplier(null); }}
            onSuccess={handleDrawerSuccess}
            supplier={selectedSupplier}
            onShowToast={showToast}
          />
          <SupplierViewDrawer
            open={viewDrawerOpen}
            onClose={() => { setViewDrawerOpen(false); setSelectedSupplier(null); }}
            supplier={selectedSupplier}
            onEdit={() => { setViewDrawerOpen(false); setEditDrawerOpen(true); }}
            onDelete={async () => {
              setViewDrawerOpen(false);
              await handleDeleteSupplier(selectedSupplier);
              setSelectedSupplier(null);
            }}
          />
        </>
      )}

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Supplier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete supplier{' '}
            <strong>"{deleteTarget?.name}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialogLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen deleting overlay (mirrors EnhancedProductDrawer) ── */}
      <Backdrop
        open={deletingOverlay}
        sx={{
          zIndex: (t) => t.zIndex.modal + 100,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Deleting Supplier…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default SuppliersManagement;